import { unlink, rm } from 'node:fs/promises';
import { resolve } from 'node:path';
import { getDataDirPath } from '@wix/cli-data-dir';
import type { VariantOf } from 'variant';
import { match, fields, variant, payload, isType } from 'variant';
import { pathExists, readJson, writeJson } from '@wix/cli-fs';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { ApiKeyAuth, AccountAuth, SiteAuth, UserInfo } from './schemas.js';
import {
  apiKeyAuthSchema,
  accountAuthSchema,
  siteAuthSchema,
} from './schemas.js';

function getLegacyAuthDataFilePath() {
  return resolve(getDataDirPath(), 'auth.json');
}

function getAuthDataFolder() {
  return resolve(getDataDirPath(), 'auth');
}

function getApiKeyAuthDataFilePath() {
  return resolve(getAuthDataFolder(), 'api-key.json');
}

function getAccountAuthDataFilePath() {
  return resolve(getAuthDataFolder(), 'account.json');
}

function getSiteAuthDataFilePath(siteId: string) {
  return resolve(getAuthDataFolder(), `${siteId}.json`);
}

const ApiKeyAuthData = variant({
  Authenticated: payload<ApiKeyAuth>(),
  Unauthenticated: {},
});

type ApiKeyAuthData = VariantOf<typeof ApiKeyAuthData>;

export async function readApiKeyAuthData(): Promise<ApiKeyAuthData> {
  const apiKeyAuthDataFilePath = getApiKeyAuthDataFilePath();

  if (!(await pathExists(apiKeyAuthDataFilePath))) {
    return ApiKeyAuthData.Unauthenticated();
  }

  const apiKeyFileContents = await readJson(apiKeyAuthDataFilePath);
  const savedApiKeyAuthData = apiKeyAuthSchema.parse(apiKeyFileContents);

  return ApiKeyAuthData.Authenticated(savedApiKeyAuthData);
}

export async function saveApiKeyAuthData(authData: ApiKeyAuth) {
  try {
    const targetFilePath = getApiKeyAuthDataFilePath();

    await writeJson(targetFilePath, authData);
  } catch (error) {
    throw new CliError({
      code: CliErrorCode.FailedToSaveApiKeyAuthData(),
      cause: error,
    });
  }
}

export const AccountAuthData = variant({
  Authenticated: fields<AccountAuth>(),
  Unauthenticated: {},
  Expired: fields<AccountAuth>(),
});
export type AccountAuthData = VariantOf<typeof AccountAuthData>;

export async function readAccountAuthData(): Promise<AccountAuthData> {
  const accountAuthDataFilePath = getAccountAuthDataFilePath();

  if (!(await pathExists(accountAuthDataFilePath))) {
    return AccountAuthData.Unauthenticated();
  }

  const authFileContents = await readJson(accountAuthDataFilePath);
  const savedAccountAuthData = accountAuthSchema.parse(authFileContents);

  if (!isValidToken(savedAccountAuthData)) {
    return AccountAuthData.Expired(savedAccountAuthData);
  }

  return AccountAuthData.Authenticated(savedAccountAuthData);
}

const SiteAuthData = variant({
  Authenticated: fields<SiteAuth>(),
  Unauthenticated: {},
  Expired: fields<SiteAuth>(),
});

type SiteAuthData = VariantOf<typeof SiteAuthData>;

export async function readSiteAuthData(siteId: string): Promise<SiteAuthData> {
  const siteAuthDataFilePath = getSiteAuthDataFilePath(siteId);

  if (!(await pathExists(siteAuthDataFilePath))) {
    return SiteAuthData.Unauthenticated();
  }

  const authFileContents = await readJson(siteAuthDataFilePath);
  const savedSiteAuthData = siteAuthSchema.parse(authFileContents);

  if (!isValidToken(savedSiteAuthData)) {
    return SiteAuthData.Expired(savedSiteAuthData);
  }

  return SiteAuthData.Authenticated(savedSiteAuthData);
}

export async function saveAuthData(
  authData: AccountAuth | SiteAuth,
  siteId?: string
) {
  try {
    const targetFilePath = siteId
      ? getSiteAuthDataFilePath(siteId)
      : getAccountAuthDataFilePath();

    await writeJson(targetFilePath, authData);
  } catch (error) {
    throw new CliError({
      code: CliErrorCode.FailedToSaveAuthData(),
      cause: error,
    });
  }
}

export async function deleteAuthData() {
  try {
    await rm(getAuthDataFolder(), { recursive: true, force: true });
  } catch (error) {
    throw new CliError({
      code: CliErrorCode.FailedToDeleteAuthData(),
      cause: error,
    });
  }

  if (await hasLegacyAuthData()) {
    await deleteLegacyAuthData();
  }
}

async function deleteLegacyAuthData() {
  try {
    await unlink(getLegacyAuthDataFilePath());
  } catch (error) {
    throw new CliError({
      code: CliErrorCode.FailedToDeleteLegacyAuthData(),
      cause: error,
    });
  }
}

export async function hasLegacyAuthData() {
  return pathExists(getLegacyAuthDataFilePath());
}

export function isValidToken(authData: {
  expiresIn: number;
  issuedAt: number;
}): boolean {
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const tokenOffset = 10 * 60; // 10 minutes
  const tokenLifetime = authData.expiresIn - tokenOffset;
  const tokenGenerationTimestamp = authData.issuedAt;

  return tokenGenerationTimestamp + tokenLifetime > currentTimestamp;
}

export async function getUserInfo(): Promise<UserInfo | null> {
  const apiKeyAuthData = await readApiKeyAuthData();

  if (isType(apiKeyAuthData, ApiKeyAuthData.Authenticated)) {
    return apiKeyAuthData.payload.userInfo;
  }

  const accountAuthData = await readAccountAuthData();
  return match(accountAuthData, {
    Authenticated: ({ userInfo }) => userInfo,
    Expired: ({ userInfo }) => userInfo,
    Unauthenticated: () => null,
  });
}
